import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ShoppingBag,
  Clock,
  ShieldCheck,
  Sparkles,
  AlertCircle,
  Scale,
  Check,
} from 'lucide-react'
import { api, formatMoney, type ProductDetail as Product, type VariantView } from '../api/client'
import { useCart } from '../state/cart'
import { useTranslation } from '../i18n/LanguageContext'

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { t, isHindi } = useTranslation()
  const [product, setProduct] = useState<Product | null>(null)
  const [availability, setAvailability] = useState<Record<string, number>>({})
  const [selected, setSelected] = useState<VariantView | null>(null)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [justAdded, setJustAdded] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const { add } = useCart()
  const navigate = useNavigate()

  useEffect(() => {
    if (!slug) {
      return
    }

    api.catalogue
      .product(slug)
      .then(async (detail) => {
        setProduct(detail)
        setSelected(detail.variants[0] ?? null)

        if (detail.variants.length > 0) {
          try {
            const rows = await api.catalogue.availability(detail.variants.map((variant) => variant.id))
            setAvailability(
              rows.reduce<Record<string, number>>((acc, row) => {
                acc[row.variantId] = row.available
                return acc
              }, {}),
            )
          } catch {
            setAvailability(
              detail.variants.reduce<Record<string, number>>((acc, v) => {
                acc[v.id] = 10
                return acc
              }, {}),
            )
          }
        }
      })
      .catch((failure: Error) => setError(failure.message))
  }, [slug])

  if (error) {
    return (
      <div className="card p-8 text-center max-w-md mx-auto my-12">
        <AlertCircle className="mx-auto h-10 w-10 text-red-500 mb-3" />
        <h2 className="text-base font-bold text-stone-900">{t('detail_not_found')}</h2>
        <p className="mt-1 text-xs text-stone-500">{error}</p>
        <Link to="/" className="btn-primary mt-4">
          {t('detail_back')}
        </Link>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse space-y-6">
        <div className="h-6 w-32 rounded bg-stone-200" />
        <div className="grid gap-8 md:grid-cols-2">
          <div className="aspect-square rounded-3xl bg-stone-200" />
          <div className="space-y-4">
            <div className="h-8 w-3/4 rounded bg-stone-200" />
            <div className="h-5 w-1/3 rounded bg-stone-200" />
            <div className="h-20 w-full rounded bg-stone-200" />
          </div>
        </div>
      </div>
    )
  }

  const available = selected ? (availability[selected.id] ?? 10) : 0
  const isOutOfStock = available <= 0

  const hasDiscount = selected && selected.discountPercent > 0
  const activeImage = product.images[activeImageIndex] || product.images[0]

  async function handleAddToCart() {
    if (!selected || isOutOfStock || adding) return
    setAdding(true)
    setAddError(null)
    try {
      await add(selected.id, 1)
      setJustAdded(true)
      setTimeout(() => setJustAdded(false), 2500)
    } catch (err) {
      setAddError(err instanceof Error ? err.message : (isHindi ? 'टोकरी में सामान नहीं जोड़ा जा सका।' : 'Could not add item to basket.'))
    } finally {
      setAdding(false)
    }
  }

  return (
    <article className="mx-auto max-w-5xl space-y-6">
      {/* Navigation Breadcrumb */}
      <div className="flex items-center justify-between text-xs">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-bold text-emerald-800 hover:text-emerald-900 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('detail_back')}</span>
        </Link>

        <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-900">
          {product.categoryName}
        </span>
      </div>

      {/* Main Product Container */}
      <div className="grid gap-8 rounded-3xl border border-stone-200/80 bg-white p-5 shadow-sm sm:p-8 md:grid-cols-2">
        {/* Left Column: Images */}
        <div className="space-y-4">
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-stone-100 bg-stone-50">
            {activeImage ? (
              <img
                src={activeImage.url}
                alt={activeImage.altText || product.name}
                className="h-full w-full object-cover transition-all"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-stone-100 text-stone-300">
                <ShoppingBag className="h-20 w-20 stroke-[1]" />
              </div>
            )}

            {/* Discount Badge */}
            {hasDiscount && (
              <span className="absolute left-3 top-3 rounded-xl bg-emerald-600 px-2.5 py-1 text-xs font-black uppercase text-white shadow-md">
                {selected.discountPercent}% OFF
              </span>
            )}

            {/* Express delivery tag */}
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-lg bg-white/95 px-2.5 py-1 text-xs font-bold text-stone-800 backdrop-blur-md shadow-sm">
              <Clock className="h-3.5 w-3.5 text-emerald-600" />
              {isHindi ? '15–20 मिनट में डिलीवरी' : 'Delivery in 15–20 mins'}
            </span>
          </div>

          {/* Thumbnail list if multiple images */}
          {product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {product.images.map((img, idx) => (
                <button
                  key={img.id || idx}
                  type="button"
                  onClick={() => setActiveImageIndex(idx)}
                  className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-all ${
                    idx === activeImageIndex
                      ? 'border-emerald-600 ring-2 ring-emerald-500/20'
                      : 'border-stone-200 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Details & CTA */}
        <div className="flex flex-col justify-between space-y-5">
          <div className="space-y-3">
            {product.brand && (
              <p className="text-xs font-extrabold uppercase tracking-widest text-emerald-700">
                {product.brand}
              </p>
            )}

            <h1 className="font-heading text-2xl font-black tracking-tight text-stone-900 sm:text-3xl">
              {product.name}
            </h1>

            {/* Pricing */}
            {selected && (
              <div className="flex items-baseline gap-3 pt-1">
                <span className="money text-2xl font-black text-stone-900 sm:text-3xl">
                  {formatMoney(selected.sellingPricePaise)}
                </span>

                {hasDiscount && (
                  <span className="money text-base text-stone-400 line-through">
                    {formatMoney(selected.mrpPaise)}
                  </span>
                )}

                <span className="text-xs font-semibold text-emerald-700">
                  {isHindi ? '(सभी कर सहित)' : '(Inclusive of all taxes)'}
                </span>
              </div>
            )}

            {product.description && (
              <p className="text-xs leading-relaxed text-stone-600 sm:text-sm">
                {product.description}
              </p>
            )}

            {/* Pack Size Selector */}
            <div className="space-y-2 pt-2">
              <label className="label">{t('detail_qty_select')}</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {product.variants.map((variant) => {
                  const isCur = variant.id === selected?.id
                  const variantRemaining = availability[variant.id] ?? 10
                  const isSoldOut = variantRemaining <= 0

                  return (
                    <button
                      key={variant.id}
                      type="button"
                      disabled={isSoldOut}
                      onClick={() => setSelected(variant)}
                      className={`relative flex flex-col items-start rounded-2xl border p-3 text-left transition-all ${
                        isCur
                          ? 'border-emerald-600 bg-emerald-50/70 ring-2 ring-emerald-500/20'
                          : 'border-stone-200/90 bg-white hover:border-emerald-300'
                      } ${isSoldOut ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-xs font-bold text-stone-900">{variant.name}</span>
                      <span className="money mt-0.5 text-xs font-semibold text-emerald-800">
                        {formatMoney(variant.sellingPricePaise)}
                      </span>
                      {variant.discountPercent > 0 && (
                        <span className="mt-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded px-1">
                          {variant.discountPercent}% OFF
                        </span>
                      )}
                      {isSoldOut && (
                        <span className="mt-1 text-[10px] font-bold text-red-600">
                          {t('product_out_of_stock')}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Loose item weighing alert */}
            {selected?.requiresWeighing && (
              <div className="flex items-start gap-2.5 rounded-2xl bg-amber-50 p-3.5 text-xs text-amber-900">
                <Scale className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div>
                  <span className="font-bold">{isHindi ? 'वज़न के अनुसार: ' : 'Sold by Weight: '}</span>
                  {isHindi
                    ? 'वास्तविक वज़न स्टोर पार्टनर द्वारा पैक करते समय तौला और सत्यापित किया जाएगा।'
                    : 'Exact item weight will be checked and verified when packed by our store partner.'}
                </div>
              </div>
            )}
          </div>

          {/* Action Row */}
          <div className="space-y-4 pt-4 border-t border-stone-100">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className={`btn-primary flex-1 py-3 text-base font-bold shadow-lg ${
                  justAdded
                    ? 'bg-emerald-600 shadow-emerald-700/30'
                    : 'shadow-emerald-700/20'
                }`}
                disabled={!selected || isOutOfStock || adding}
                onClick={() => void handleAddToCart()}
              >
                {adding ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t('detail_adding')}
                  </span>
                ) : justAdded ? (
                  <span className="inline-flex items-center gap-2 font-bold">
                    <Check className="h-5 w-5" /> {t('detail_added')}
                  </span>
                ) : isOutOfStock ? (
                  t('product_out_of_stock')
                ) : (
                  <span className="inline-flex items-center gap-2 font-bold">
                    <ShoppingBag className="h-5 w-5" /> {t('detail_add_btn')}
                  </span>
                )}
              </button>

              {justAdded && (
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="btn-secondary py-3 text-xs font-bold text-emerald-800 border-emerald-300 bg-emerald-50"
                >
                  {isHindi ? 'टोकरी देखें' : 'View Basket'}
                </button>
              )}
            </div>

            {addError && (
              <div role="alert" className="rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-700">
                {addError}
              </div>
            )}

            {/* Service & Quality Assurances */}
            <div className="grid grid-cols-2 gap-3 text-stone-600 text-xs">
              <div className="flex items-center gap-2 rounded-xl bg-stone-50 p-2.5">
                <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>{isHindi ? '100% गुणवत्ता आश्वासन' : '100% Quality Assurance'}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-stone-50 p-2.5">
                <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
                <span>{isHindi ? 'त्वरित रिप्लेसमेंट गारंटी' : 'Instant Replacement Guarantee'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}
